<script setup lang="ts">
import { onMounted, ref } from 'vue'
import { type ClientReport, clientReportsApi } from '@/api/entities/review/clientReports'

definePage({
  meta: { title: 'Client Reports', action: 'read', subject: 'review' },
})

const { handleError } = useErrorHandler()
const loading = ref(false)
const items = ref<ClientReport[]>([])
const currentPage = ref(1)
const lastPage = ref(1)

const headers = [
  { title: '클라이언트명', key: 'client_name', sortable: false },
  { title: '리포트 상태', key: 'status', sortable: false },
  { title: '리포트 날짜', key: 'report_date', sortable: false },
  { title: '관련 링크', key: 'report_url', sortable: false },
  { title: '생성일', key: 'created_at', sortable: false },
]

const statusColor = (status: string) => {
  const map: Record<string, string> = {
    completed: 'success',
    pending: 'warning',
    failed: 'error',
    processing: 'info',
  }

  
  return map[status] ?? 'default'
}

const loadItems = async () => {
  loading.value = true
  try {
    const { success, data } = await clientReportsApi.list({ page: currentPage.value })
    if (success && data) {
      items.value = data.data
      lastPage.value = data.last_page
    }
  }
  catch (error) {
    handleError(error, { showNotification: false })
  }
  finally {
    loading.value = false
  }
}

const onPageChange = (page: number) => {
  currentPage.value = page
  loadItems()
}

const formatDate = (d: string | null) => d ? new Date(d).toLocaleString('ko-KR') : '-'

onMounted(loadItems)
</script>

<template>
  <VCard>
    <VCardTitle class="pa-4">
      Client Reports
    </VCardTitle>
    <VCardText>
      <VDataTable
        :headers="headers"
        :items="items"
        :loading="loading"
        hide-default-footer
      >
        <template #item.status="{ item }">
          <VChip
            :color="statusColor(item.status)"
            size="small"
          >
            {{ item.status }}
          </VChip>
        </template>
        <template #item.report_date="{ item }">
          {{ item.report_date ?? '-' }}
        </template>
        <template #item.report_url="{ item }">
          <a
            v-if="item.report_url"
            :href="item.report_url"
            target="_blank"
            rel="noopener noreferrer"
            class="text-decoration-none"
          >
            링크
          </a>
          <span
            v-else
            class="text-disabled"
          >-</span>
        </template>
        <template #item.created_at="{ item }">
          {{ formatDate(item.created_at) }}
        </template>
        <template #no-data>
          <div class="text-center py-8">
            데이터가 없습니다.
          </div>
        </template>
      </VDataTable>
      <div
        v-if="lastPage > 1"
        class="d-flex justify-center mt-4"
      >
        <VPagination
          :model-value="currentPage"
          :length="lastPage"
          @update:model-value="onPageChange"
        />
      </div>
    </VCardText>
  </VCard>
</template>
